import React, { useEffect, useMemo } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, List, ListItem, ListItemText, Paper } from '@mui/material';
import { useBreadcrumb } from '../../context/BreadcrumbContext';

interface SearchResult {
  title: string;
  description: string;
  path: string;
}

const allPossibleResults: SearchResult[] = [
  {
    title: 'Workers List',
    description: 'View and manage all workers.',
    path: '/workers',
  },
  {
    title: 'Facilities List',
    description: 'View and manage all facilities.',
    path: '/facilities',
  },
  {
    title: 'Shifts',
    description: 'View and manage shifts.',
    path: '/shifts',
  },
  {
    title: 'Reports',
    description: 'Generate and view reports.',
    path: '/reports',
  },
  {
    title: 'Settings',
    description: 'Configure application settings.',
    path: '/settings',
  },
  {
    title: 'Dashboard',
    description: 'Go to the main dashboard.',
    path: '/dashboard',
  },
  {
    title: 'Profile',
    description: 'View your profile.',
    path: '/profile',
  }
];

const SearchResultsPage: React.FC = () => {
  const { setPath } = useBreadcrumb();
  const location = useLocation();
  const query = new URLSearchParams(location.search).get('q') || '';

  useEffect(() => {
    setPath([
      { title: 'Home', path: '/' },
      { title: 'Search', path: '' },
    ]);
  }, [setPath]);

  const filteredResults = useMemo(() => {
    if (!query) {
      return [];
    }
    const lowerCaseQuery = query.toLowerCase();
    return allPossibleResults.filter(
      (result) =>
        result.title.toLowerCase().includes(lowerCaseQuery) ||
        result.description.toLowerCase().includes(lowerCaseQuery)
    );
  }, [query]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Search Results for "{query}"
      </Typography>
      <Paper>
        <List>
          {filteredResults.length > 0 ? (
            filteredResults.map((result) => (
              <ListItem
                key={result.path}
                component={RouterLink}
                to={result.path}
              >
                <ListItemText
                  primary={result.title}
                  secondary={result.description}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="No results found." />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default SearchResultsPage;
